/**
 * Coach Dashboard - Main landing page for coaches
 * 
 * Features (Branch Admin Style):
 * - Quick stats grid (teams, players, session templates, active sessions)
 * - Quick action cards for templates and team management
 * - My Teams section (teams assigned to this coach)
 * - Session Templates management card
 * 
 * Follows AdminPageLayout pattern similar to BranchDashboard
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminPageLayout, Section, Card } from '../components';
import Button from '../components/Button';
import LoadingState from '../components/LoadingState';
import StatCard from '../components/StatCard';
import StatGrid from '../components/StatGrid';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { FileText, Plus, ChevronRight, Users } from 'lucide-react';

const CoachDashboard = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { showError } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [coachData, setCoachData] = useState({
    teamsCount: 0,
    playersCount: 0,
    templatesCount: 0,
    activeSessionsCount: 0,
    teams: []
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      if (!authLoading && isAuthenticated && user) {
        try {
          setLoading(true);
          // TODO: Replace with actual API calls
          // const response = await apiService.getCoachDashboardStats(user.id);
          const data = {
            teamsCount: 0,
            playersCount: 0,
            templatesCount: 0,
            activeSessionsCount: 0,
            teams: []
          };
          setCoachData(data);
        } catch (error) {
          showError('Failed to load dashboard data');
        } finally {
          setLoading(false);
        }
      }
    };

    loadDashboardData();
  }, [authLoading, isAuthenticated, user, showError]);

  if (authLoading || loading) {
    return (
      <AdminPageLayout
        title="COACH DASHBOARD"
        subtitle="Loading your coaching dashboard..."
        backButton={false}
      >
        <LoadingState />
      </AdminPageLayout>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AdminPageLayout
      title={`${user?.first_name?.toUpperCase() || 'COACH'} DASHBOARD`}
      subtitle="Your coaching and session management dashboard"
      backButton={false}
      maxWidth="1400px"
    >
      {/* Stats Grid */}
      <Section title="STATISTICS" style={{ marginBottom: '2rem' }}>
        <StatGrid>
          <StatCard
            number={coachData.teamsCount}
            label="Teams"
            detail="Teams assigned"
          />
          <StatCard
            number={coachData.playersCount}
            label="Players"
            detail="Active players"
          />
          <StatCard
            number={coachData.templatesCount}
            label="Templates"
            detail="Session templates"
          />
          <StatCard
            number={coachData.activeSessionsCount}
            label="Active Sessions"
            detail="Live sessions"
          />
        </StatGrid>
      </Section>

      {/* Session Templates Management Card */}
      <Section title="SESSION TEMPLATES" style={{ marginBottom: '2rem' }}>
        <Card level={2} style={{
          padding: '2rem',
          background: 'linear-gradient(135deg, rgba(66, 135, 245, 0.05) 0%, rgba(66, 135, 245, 0.02) 100%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '2rem'
        }}>
          <div style={{ flex: 1, minWidth: '250px' }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.2rem', fontWeight: 600 }}>
              Create & Manage Session Templates
            </h3>
            <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.95rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
              Build reusable training templates for your teams. Create private templates just for you, or create branch-wide templates (branch admins only) that other coaches can use.
            </p>
            <div style={{
              display: 'grid',
              gap: '0.5rem',
              fontSize: '0.9rem',
              color: 'var(--color-text-secondary)'
            }}>
              <div>✓ Private templates - Only you can access</div>
              <div>✓ Branch-wide templates - All coaches can view</div>
              <div>✓ Use templates to create events on team calendars</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column', minWidth: '200px' }}>
            <Button
              variant="primary"
              onClick={() => navigate('/coach/templates')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
            >
              <FileText size={18} /> View Templates
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/coach/templates/create')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
            >
              <Plus size={18} /> Create New
            </Button>
          </div>
        </Card>
      </Section>

      {/* My Teams Section */}
      <Section title="MY TEAMS">
        {coachData.teams && coachData.teams.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '1.5rem'
          }}>
            {coachData.teams.map((team) => (
              <Card key={team.id} level={2}>
                <div style={{ padding: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: 600 }}>
                    {team.name}
                  </h4>
                  <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                    {team.program_name} • {team.players_count || 0} players
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/coach/teams/${team.id}`)}
                    style={{ width: '100%' }}
                  >
                    View Team <ChevronRight size={16} style={{ marginLeft: '0.5rem' }} />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card level={1}>
            <div style={{
              padding: '3rem 1.5rem',
              textAlign: 'center',
              background: 'var(--color-background-lighter, #f9f9f9)',
              borderRadius: '8px'
            }}>
              <Users size={48} style={{
                margin: '0 auto 1rem',
                color: 'var(--color-text-secondary)',
                opacity: 0.5
              }} />
              <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--color-text-secondary)' }}>
                No Teams Assigned Yet
              </h4>
              <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                Contact your branch admin to assign you to coaching teams.
              </p>
            </div>
          </Card>
        )}
      </Section>
    </AdminPageLayout>
  );
};

export default CoachDashboard;
